import { useCallback, useEffect, useRef } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";
import type { GridLayoutProps, Layout, LayoutItem } from "react-grid-layout";

import {
  buildColumnDropLayout,
  buildPushedDropLayout,
  getSwapTargetId,
  resolveLayoutOverlaps,
} from "src/helpers/builderLayout";
import useBuilderStore from "src/store/builderStore";

const DRAG_AUTO_SCROLL_EDGE_OFFSET = 168;
const DRAG_AUTO_SCROLL_MAX_STEP = 32;
const DRAG_AUTO_SCROLL_MIN_STEP = 4;
const DRAG_AUTO_SCROLL_SCREEN_GUTTER = 56;

const GRID_DRAG_CANCEL_SELECTOR =
  ".builder-canvas-card__action, .builder-canvas-card__body, .builder-canvas-card__content, .builder-canvas-card__dropzone, .builder-canvas-card__placeholder";

const isScrollableElement = (element: HTMLElement) => {
  const computedStyle = window.getComputedStyle(element);
  const overflowY = computedStyle.overflowY;

  return (
    ["auto", "scroll", "overlay"].includes(overflowY) &&
    element.scrollHeight > element.clientHeight
  );
};

const getScrollableContainers = (element: HTMLElement | null) => {
  const containers: HTMLElement[] = [];
  let currentElement = element?.parentElement ?? null;

  while (currentElement) {
    if (isScrollableElement(currentElement)) {
      containers.push(currentElement);
    }

    currentElement = currentElement.parentElement;
  }

  const documentScrollElement = document.scrollingElement as HTMLElement | null;

  if (
    documentScrollElement &&
    !containers.some((container) => container === documentScrollElement)
  ) {
    containers.push(documentScrollElement);
  }

  return containers;
};

const isDocumentScrollContainer = (element: HTMLElement) =>
  element === document.scrollingElement ||
  element === document.documentElement ||
  element === document.body;

const getScrollContainerTop = (element: HTMLElement) =>
  isDocumentScrollContainer(element) ? window.scrollY : element.scrollTop;

const getScrollContainerClientHeight = (element: HTMLElement) =>
  isDocumentScrollContainer(element) ? window.innerHeight : element.clientHeight;

const getScrollContainerHeight = (element: HTMLElement) =>
  isDocumentScrollContainer(element)
    ? Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
    : element.scrollHeight;

const scrollContainerBy = (element: HTMLElement, scrollStep: number) => {
  if (isDocumentScrollContainer(element)) {
    window.scrollBy({ top: scrollStep, behavior: "auto" });
    return;
  }

  element.scrollBy({ top: scrollStep, behavior: "auto" });
};

const getScrollContainerVisibleEdges = (element: HTMLElement) => {
  if (isDocumentScrollContainer(element)) {
    return {
      top: DRAG_AUTO_SCROLL_SCREEN_GUTTER,
      bottom: window.innerHeight - DRAG_AUTO_SCROLL_SCREEN_GUTTER,
    };
  }

  const containerRect = element.getBoundingClientRect();

  return {
    top: Math.max(containerRect.top, DRAG_AUTO_SCROLL_SCREEN_GUTTER),
    bottom: Math.min(
      containerRect.bottom,
      window.innerHeight - DRAG_AUTO_SCROLL_SCREEN_GUTTER,
    ),
  };
};

export type BuilderGridInteractionProps = Pick<
  GridLayoutProps,
  | "dragConfig"
  | "resizeConfig"
  | "onDragStart"
  | "onDrag"
  | "onDragStop"
  | "onResizeStart"
  | "onResizeStop"
>;

type UseBuilderGridInteractionsParams = {
  pageRef: RefObject<HTMLElement | null>;
  selectedLayoutPreset: string;
  commitCanvasLayout: (layout: Layout) => void;
  swapCanvasItems: (sourceItemId: string, targetItemId: string) => void;
  buildPresetLayout: (layoutItems: Layout, presetId: string) => LayoutItem[];
  setActiveCanvasInteractionItemId: Dispatch<SetStateAction<string | null>>;
};

export default function useBuilderGridInteractions({
  pageRef,
  selectedLayoutPreset,
  commitCanvasLayout,
  swapCanvasItems,
  buildPresetLayout,
  setActiveCanvasInteractionItemId,
}: UseBuilderGridInteractionsParams): BuilderGridInteractionProps {
  const isPresetLayoutMode = selectedLayoutPreset !== "";
  const dragStartLayoutRef = useRef<Layout>([]);
  const dragAutoScrollFrameRef = useRef<number | null>(null);
  const dragPointerClientYRef = useRef<number | null>(null);
  const dragScrollContainersRef = useRef<HTMLElement[]>([]);
  const activeDragScrollContainerRef = useRef<HTMLElement | null>(null);

  const handleDocumentMouseMove = useCallback((event: MouseEvent) => {
    dragPointerClientYRef.current = event.clientY;
  }, []);

  const stopDragAutoScroll = useCallback(() => {
    dragPointerClientYRef.current = null;
    dragScrollContainersRef.current = [];
    activeDragScrollContainerRef.current = null;

    document.removeEventListener("mousemove", handleDocumentMouseMove);

    if (dragAutoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(dragAutoScrollFrameRef.current);
      dragAutoScrollFrameRef.current = null;
    }
  }, [handleDocumentMouseMove]);

  const getDragAutoScrollStep = useCallback(
    (pointerClientY: number, scrollContainer: HTMLElement) => {
      const { top, bottom } = getScrollContainerVisibleEdges(scrollContainer);
      const topDistance = pointerClientY - top;
      const bottomDistance = bottom - pointerClientY;
      const scrollTop = getScrollContainerTop(scrollContainer);
      const clientHeight = getScrollContainerClientHeight(scrollContainer);
      const scrollHeight = getScrollContainerHeight(scrollContainer);

      if (topDistance < DRAG_AUTO_SCROLL_EDGE_OFFSET && scrollTop > 0) {
        const intensity =
          (DRAG_AUTO_SCROLL_EDGE_OFFSET - Math.max(topDistance, 0)) /
          DRAG_AUTO_SCROLL_EDGE_OFFSET;

        return -Math.max(
          DRAG_AUTO_SCROLL_MIN_STEP,
          intensity * intensity * DRAG_AUTO_SCROLL_MAX_STEP,
        );
      }

      if (
        bottomDistance < DRAG_AUTO_SCROLL_EDGE_OFFSET &&
        scrollTop + clientHeight < scrollHeight
      ) {
        const intensity =
          (DRAG_AUTO_SCROLL_EDGE_OFFSET - Math.max(bottomDistance, 0)) /
          DRAG_AUTO_SCROLL_EDGE_OFFSET;

        return Math.max(
          DRAG_AUTO_SCROLL_MIN_STEP,
          intensity * intensity * DRAG_AUTO_SCROLL_MAX_STEP,
        );
      }

      return 0;
    },
    [],
  );

  const runDragAutoScroll = useCallback(() => {
    const pointerClientY = dragPointerClientYRef.current;

    if (pointerClientY === null) {
      dragAutoScrollFrameRef.current = null;
      return;
    }

    let activeScrollContainer = activeDragScrollContainerRef.current;

    if (
      activeScrollContainer &&
      getDragAutoScrollStep(pointerClientY, activeScrollContainer) === 0
    ) {
      activeScrollContainer = null;
      activeDragScrollContainerRef.current = null;
    }

    if (!activeScrollContainer) {
      activeScrollContainer =
        dragScrollContainersRef.current.find(
          (scrollContainer) =>
            getDragAutoScrollStep(pointerClientY, scrollContainer) !== 0,
        ) ?? null;
      activeDragScrollContainerRef.current = activeScrollContainer;
    }

    if (activeScrollContainer) {
      const scrollStep = getDragAutoScrollStep(
        pointerClientY,
        activeScrollContainer,
      );
      const previousScrollTop = getScrollContainerTop(activeScrollContainer);

      if (scrollStep !== 0) {
        scrollContainerBy(activeScrollContainer, scrollStep);
      }

      if (getScrollContainerTop(activeScrollContainer) === previousScrollTop) {
        activeDragScrollContainerRef.current = null;
      }
    }

    dragAutoScrollFrameRef.current = window.requestAnimationFrame(
      runDragAutoScroll,
    );
  }, [getDragAutoScrollStep]);

  const startDragAutoScroll = useCallback(
    (event: Event, dragElement?: HTMLElement) => {
      if (!(event instanceof MouseEvent)) {
        return;
      }

      stopDragAutoScroll();
      dragPointerClientYRef.current = event.clientY;
      dragScrollContainersRef.current = getScrollableContainers(
        dragElement ?? pageRef.current,
      );
      document.addEventListener("mousemove", handleDocumentMouseMove, {
        passive: true,
      });
      dragAutoScrollFrameRef.current = window.requestAnimationFrame(
        runDragAutoScroll,
      );
    },
    [handleDocumentMouseMove, pageRef, runDragAutoScroll, stopDragAutoScroll],
  );

  const handleGridDragStart: GridLayoutProps["onDragStart"] = (
    nextLayout,
    _oldItem,
    newItem,
    _placeholder,
    event,
    element,
  ) => {
    dragStartLayoutRef.current = nextLayout.map((item) => ({
      ...item,
    }));
    setActiveCanvasInteractionItemId(newItem?.i ?? null);
    startDragAutoScroll(event, element);
  };

  const handleGridDrag: GridLayoutProps["onDrag"] = (
    _nextLayout,
    _oldItem,
    _newItem,
    _placeholder,
    event,
  ) => {
    if (event instanceof MouseEvent) {
      dragPointerClientYRef.current = event.clientY;
    }
  };

  const handleDragStopCleanup = () => {
    setActiveCanvasInteractionItemId(null);
    stopDragAutoScroll();
    dragStartLayoutRef.current = [];
  };

  const handlePresetDragStop: GridLayoutProps["onDragStop"] = (
    nextLayout,
    _oldItem,
    newItem,
  ) => {
    if (!isPresetLayoutMode) {
      handleDragStopCleanup();
      commitCanvasLayout(resolveLayoutOverlaps(nextLayout, newItem?.i ?? null));
      return;
    }

    setActiveCanvasInteractionItemId(null);
    stopDragAutoScroll();

    const presetId = selectedLayoutPreset;

    const columnDropLayout = buildColumnDropLayout(
      dragStartLayoutRef.current,
      nextLayout,
      newItem?.i,
    );

    if (columnDropLayout) {
      commitCanvasLayout(columnDropLayout);
      dragStartLayoutRef.current = [];
      return;
    }

    const swapTargetId = getSwapTargetId(dragStartLayoutRef.current, newItem);
    const swapTargetCanvasItem = swapTargetId
      ? useBuilderStore
          .getState()
          .canvasItems.find((item) => item.i === swapTargetId)
      : null;
    const pushedDropLayout =
      swapTargetCanvasItem?.assignedWidgetId
        ? buildPushedDropLayout(
            dragStartLayoutRef.current,
            nextLayout,
            newItem?.i,
          )
        : null;

    if (pushedDropLayout) {
      commitCanvasLayout(pushedDropLayout);
      dragStartLayoutRef.current = [];
      return;
    }

    if (swapTargetId && newItem?.i) {
      swapCanvasItems(newItem.i, swapTargetId);
      window.requestAnimationFrame(() => {
        const swappedLayout: Layout = useBuilderStore
          .getState()
          .canvasItems.map(
            ({ templateId, label, category, description, ...item }) => item,
          );

        if (swappedLayout.length > 0) {
          commitCanvasLayout(buildPresetLayout(swappedLayout, presetId));
        }
      });
      dragStartLayoutRef.current = [];
      return;
    }

    commitCanvasLayout(buildPresetLayout(nextLayout, presetId));
    dragStartLayoutRef.current = [];
  };

  const handleFreeStyleDragStop: GridLayoutProps["onDragStop"] = (
    nextLayout,
    _oldItem,
    newItem,
  ) => {
    handleDragStopCleanup();
    commitCanvasLayout(resolveLayoutOverlaps(nextLayout, newItem?.i ?? null));
  };

  const handleFreeStyleResizeStart: GridLayoutProps["onResizeStart"] = (
    _nextLayout,
    _oldItem,
    newItem,
  ) => {
    setActiveCanvasInteractionItemId(newItem?.i ?? null);
  };

  const handleFreeStyleResizeStop: GridLayoutProps["onResizeStop"] = (
    nextLayout,
    _oldItem,
    newItem,
  ) => {
    setActiveCanvasInteractionItemId(null);
    commitCanvasLayout(resolveLayoutOverlaps(nextLayout, newItem?.i ?? null));
  };

  useEffect(
    () => () => {
      stopDragAutoScroll();
    },
    [stopDragAutoScroll],
  );

  const freeStyleGridInteractionConfig: BuilderGridInteractionProps = {
    dragConfig: {
      enabled: true,
      cancel: GRID_DRAG_CANCEL_SELECTOR,
    },
    resizeConfig: {
      enabled: true,
      handles: ["se"],
    },
    onDragStart: handleGridDragStart,
    onDrag: handleGridDrag,
    onDragStop: handleFreeStyleDragStop,
    onResizeStart: handleFreeStyleResizeStart,
    onResizeStop: handleFreeStyleResizeStop,
  };

  const presetGridInteractionConfig: BuilderGridInteractionProps = {
    dragConfig: {
      enabled: true,
      cancel: GRID_DRAG_CANCEL_SELECTOR,
    },
    resizeConfig: {
      enabled: false,
      handles: ["se"],
    },
    onDragStart: handleGridDragStart,
    onDrag: handleGridDrag,
    onDragStop: handlePresetDragStop,
    onResizeStart: undefined,
    onResizeStop: undefined,
  };

  return isPresetLayoutMode
    ? presetGridInteractionConfig
    : freeStyleGridInteractionConfig;
}
